import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add,
  Delete,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { GET_TREATMENTS, CREATE_TREATMENT, DELETE_TREATMENT } from '../graphql/operations';
import { Treatment, TreatmentInput } from '../types';
import { formatDateString } from '../utils/dateUtils';

const TreatmentList: React.FC = () => {
  const navigate = useNavigate();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState<Treatment | null>(null);
  
  const [formData, setFormData] = useState<TreatmentInput>({
    name: '',
    description: '',
    treatment_type: 'OTC',
    instructions: '',
    regular_price: undefined,
    sale_price: undefined,
    image_url: '',
    is_active: true,
  });

  const { data, loading, error, refetch } = useQuery(GET_TREATMENTS);

  const [createTreatment, { loading: creating }] = useMutation(CREATE_TREATMENT, {
    onCompleted: () => {
      setCreateDialogOpen(false);
      resetForm();
      refetch();
    },
    onError: (error) => {
      console.error('Error creating treatment:', error);
    },
  });

  const [deleteTreatment, { loading: deleting }] = useMutation(DELETE_TREATMENT, {
    onCompleted: () => {
      setDeleteDialogOpen(false);
      setSelectedTreatment(null);
      refetch();
    },
    onError: (error) => {
      console.error('Error deleting treatment:', error);
    },
  });

  const handleNavigateToEdit = (id: string) => {
    navigate(`/treatments/${id}`);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      treatment_type: 'OTC',
      instructions: '',
      regular_price: undefined,
      sale_price: undefined,
      image_url: '',
      is_active: true,
    });
  };

  const handleInputChange = (field: keyof TreatmentInput) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.type === 'checkbox' 
      ? (event.target as HTMLInputElement).checked 
      : event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: field === 'regular_price' || field === 'sale_price' 
        ? (value === '' ? undefined : parseFloat(value as string))
        : value,
    }));
  };

  const handleCreateTreatment = () => {
    createTreatment({ variables: { input: formData } });
  };

  const handleDeleteTreatment = () => {
    if (!selectedTreatment) return;
    
    deleteTreatment({ 
      variables: { id: selectedTreatment.id },
    });
  };

  const getTreatmentTypeColor = (type: string) => {
    switch (type) {
      case 'PRESCRIPTION': return 'error';
      case 'OTC': return 'primary';
      case 'HOME_REMEDY': return 'success';
      case 'LIFESTYLE': return 'info';
      case 'PROCEDURE': return 'warning';
      default: return 'default';
    }
  };

  const treatments = data?.listTreatments?.items || [];

  if (loading) return <Typography>Loading treatments...</Typography>;
  if (error) return <Alert severity="error">Error loading treatments: {error.message}</Alert>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Treatments</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/treatments/new')}
        >
          Add New Treatment
        </Button>
      </Box>

      <Grid container spacing={3}>
        {treatments.map((treatment: Treatment) => (
          <Grid item xs={12} sm={6} md={4} key={treatment.id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out'
                }
              }}
              onClick={() => handleNavigateToEdit(treatment.id)}
            >
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    {treatment.name}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Chip
                      label={treatment.is_active ? 'Active' : 'Inactive'}
                      color={treatment.is_active ? 'success' : 'default'}
                      size="small"
                    />
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent card click
                        setSelectedTreatment(treatment);
                        setDeleteDialogOpen(true);
                      }}
                      title="Delete"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
                
                <Chip
                  label={treatment.treatment_type}
                  color={getTreatmentTypeColor(treatment.treatment_type) as any}
                  size="small"
                  sx={{ mb: 2 }}
                />
                
                {treatment.description && (
                  <Typography variant="body2" paragraph>
                    {treatment.description.length > 100
                      ? `${treatment.description.substring(0, 100)}...`
                      : treatment.description}
                  </Typography>
                )}

                {(treatment.regular_price || treatment.sale_price) && (
                  <Box mb={2}>
                    {treatment.sale_price && (
                      <Typography variant="body2" color="error" component="span">
                        ${treatment.sale_price.toFixed(2)}
                      </Typography>
                    )}
                    {treatment.regular_price && (
                      <Typography 
                        variant="body2" 
                        color="textSecondary" 
                        component="span"
                        sx={{ 
                          ml: treatment.sale_price ? 1 : 0,
                          textDecoration: treatment.sale_price ? 'line-through' : 'none'
                        }}
                      >
                        ${treatment.regular_price.toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                )}

                <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                  Type: {treatment.treatment_type}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {treatments.length === 0 && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No treatments found
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/treatments/new')}
          >
            Create your first treatment
          </Button>
        </Box>
      )}

      {/* Create Treatment Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Treatment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Treatment Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Treatment Type</InputLabel>
                <Select
                  value={formData.treatment_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, treatment_type: e.target.value as any }))}
                >
                  <MenuItem value="PRESCRIPTION">Prescription</MenuItem>
                  <MenuItem value="OTC">Over the Counter</MenuItem>
                  <MenuItem value="HOME_REMEDY">Home Remedy</MenuItem>
                  <MenuItem value="LIFESTYLE">Lifestyle</MenuItem>
                  <MenuItem value="PROCEDURE">Procedure</MenuItem>
                  <MenuItem value="OTHER">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleInputChange('description')}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Instructions"
                value={formData.instructions}
                onChange={handleInputChange('instructions')}
                multiline
                rows={4}
                placeholder="Detailed usage instructions..."
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Regular Price"
                value={formData.regular_price || ''}
                onChange={handleInputChange('regular_price')}
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label="Sale Price"
                value={formData.sale_price || ''}
                onChange={handleInputChange('sale_price')}
                inputProps={{ step: 0.01, min: 0 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Image URL"
                value={formData.image_url}
                onChange={handleInputChange('image_url')}
                placeholder="https://example.com/image.jpg"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreateTreatment} 
            variant="contained"
            disabled={creating || !formData.name.trim()}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{selectedTreatment?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
              color="error" 
              variant="contained" 
              onClick={handleDeleteTreatment}
              disabled={deleting}
            >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TreatmentList;
